import { ComponentProps, ComponentType, createContext, useContext } from 'react';
import Link from 'next/link';
import BasePortableText from 'react-portable-text';
import { useRouter } from 'next/router';

import { urlFor } from '@root/lib/sanity';
import ColorOfTheYear from 'components/ColorOfTheYear';
import DoubleHeader from 'components/typography/DoubleHeader';
import FaqItem from 'components/FaqItem';
import GentuxEm from 'components/GentuxEm';
import HTODisclaimer from 'components/HTODisclaimer';
import Layout1x1 from 'components/Layout1x1';
import Layout2x1 from 'components/Layout2x1';
import Layout2x1ImageLeft from 'components/Layout2x1ImageLeft';
import Layout2x1ImageRight from 'components/Layout2x1ImageRight';
import Layout2x2 from 'components/Layout2x2';
import LayoutFaq from 'components/LayoutFaq';
import FeaturedReviews from 'components/FeaturedReviews';
import Line from 'components/Line';
import NextSteps from 'components/NextSteps';
import Picture from 'components/Picture';
import Quote from 'components/Quote';
import SalesCalloutGroomsFree from 'components/SalesCalloutGroomsFree';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutInspiration from 'components/SalesCalloutInspiration';
import SalesCalloutReviews from 'components/SalesCalloutReviews';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import SalesCalloutAlternateNew from 'components/SalesCalloutAlternateNew';
import {
  Button as ButtonProps,
  Bundle as SanityBundle,
  DoubleHeader as DoubleHeaderProps,
  FaqItem as FaqItemType,
  FeaturedReviewItem as FeaturedReviewItemType,
  FeaturedReviews as FeaturedReviewsType,
  GalleryProductBlock as GalleryProductBlockProps,
  HiwItem as HIWItemProps,
  Layout2x1ImageLeft as Layout2x1ImageLeftProps,
  Layout2x1ImageRight as Layout2x1ImageRightProps,
  Layout3x1 as Layout3x1Props,
  LayoutFaq as LayoutFaqProps,
  Layout1x1 as Layout1x1Props,
  Layout2x1 as Layout2x1Props,
  Layout2x2 as Layout2x2Props,
  NumberedList as NumberedListProps,
  NumberedListItem as NumberedListItemProps,
  Picture as PictureProps,
  Product as SanityProduct,
  ProductCallout as ProductCalloutType,
  Quote as QuoteProps,
  SalesCallout,
  SalesCalloutAlternate as SalesCalloutAlternateProps,
  Space as SpaceProps,
  StyleBlock as StyleBlockProps,
  ThreeImagesWithText as ThreeImagesWithTextProps,
  Video as VideoProps,
  VisualAsset as VisualAssetType,
  WeddingTable as WeddingTableProps,
  YotpoReviewsCarousel as YotpoReviewsCarouselProps,
  Tab as TabSanityProps,
  Tabs as TabsSanityProps,
  YotpoStarRatings as YotpoStarRatingsProps,
  YotpoUgcGallery as YotpoUgcGalleryProps,
} from '@root/sanity/sanity.types';
import type { Breakpoints, Product, SanityLayoutComponentSpacing } from '@root/src/types';
import type { SerializerData } from './serializerData';
import SanityButton from 'components/sanity/Button';
import QuoteCallout from '@root/src/components/QuoteCallout';
import Carousel from 'components/Carousel';
import GalleryProductBlock from 'components/GalleryProductBlock';
import Layout3x1 from 'components/Layout3x1';
import ProductCallout from 'components/ProductCallout';
import FitProfile from 'partials/assets/FitProfile';
import Video from 'components/Video';
import HIWItem from 'components/HIWItem';
import IllustrationDesign from 'components/IllustrationDesign';
import IllustrationFit from 'components/IllustrationFit';
import IllustrationShip from 'components/IllustrationShip';
import ThreeImagesWithText from 'components/ThreeImagesWithText';
import NumberedList from 'components/NumberedList';
import WeddingTable from 'components/WeddingTable';
import YotpoReviewsBadge from 'components/YotpoReviewsBadge';
import YotpoStarRatings from 'components/YotpoStarRatings';
import YotpoReviewsCarousel from 'components/YotpoReviewsCarousel';
import Tabs from 'components/sanity/Tabs';
import { appendQueryString } from '@root/src/utils/utils';
import YotpoImageGallery from '../yotpo/ImageGallery';

export type PortableTextProps = ComponentProps<typeof BasePortableText>;

export type Serializers = {
  [key: string]: ComponentType<any>;
};

type SerializerOptions = {
  data: SerializerData;
  serializers?: Serializers;
};

export const SerializerContext = createContext<SerializerOptions>({ data: {} });

export const useSerializerData = () => {
  const { data } = useContext(SerializerContext);

  return data;
};

/**
 * Wraps `<PortableText />` with default values for certain props
 *
 * `ignoreUnknownTypes` is set to `true` so we can conditionally disable "unknown
 * type" exceptions via the `unknownType` serializer. This will allow us to test
 * new block types locally without affecting other developers or the CI pipeline.
 *
 * `serializers` provides the component serializers that are defined below.
 */
export const PortableText = (props: PortableTextProps & { children?: any }) => {
  const context = useContext(SerializerContext);

  const serializers = {
    ...components,
    ...(context.serializers ?? {}),
    ...(props.serializers ?? {}),
  };

  return (
    <BasePortableText
      className={props.className}
      content={props.content}
      ignoreUnknownTypes={true}
      serializers={serializers}
    />
  );
};

/**
 * Returns a boolean indicating whether an unknown block type should cause an
 * exception to be thrown
 *
 * #### Behavior by environment
 * |                | Client                              | Server                                |
 * |----------------|-------------------------------------|---------------------------------------|
 * | **Local**      | Shows an error on the page          | Logs a warning to the terminal        |
 * | **QA**         | Logs an error to the console        | Logs a warning to the terminal        |
 * | **Production** | Logs an error to the console [1]    | Throws an exception and stops a build |
 *
 * [1] This condition will never be reached since the build will fail and the site
 *     won't be deployed, but if it was reached then this is how it would behave.
 */
export const shouldThrowOnUnknownType = () => typeof window !== 'undefined' || process.env.ENVIRONMENT == 'production';

const getSalesCallout = (salesCallout: SalesCallout, ctaPageName: string) => {
  switch (salesCallout.type) {
    case 'groomsFree':
      return <SalesCalloutGroomsFree ctaPageName={ctaPageName} />;
    case 'hto':
      return <SalesCalloutHTO ctaPageName={ctaPageName} />;
    case 'swatches':
      return <SalesCalloutSwatches ctaPageName={ctaPageName} />;
    case 'inspiration':
      return <SalesCalloutInspiration ctaPageName={ctaPageName} />;
    case 'reviews':
      return <SalesCalloutReviews ctaPageName={ctaPageName} />;
  }
};

const getVisualAsset = (assetType: string) => {
  switch (assetType) {
    case 'fitProfile':
      return <FitProfile />;
    case 'designIllustration':
      return <IllustrationDesign />;
    case 'fitIllustration':
      return <IllustrationFit />;
    case 'shipIllustration':
      return <IllustrationShip />;
  }
};

const getPadding = <T extends SanityLayoutComponentSpacing>(spaceBottom: T['spaceBottom'], spaceTop: T['spaceTop']) =>
  `${spaceTop === 'collapsed' ? 'pt-0' : ''} ${spaceBottom === 'collapsed' ? 'pb-0' : ''}`.trim();

export const components = {
  unknownType: (props: any) => {
    if (shouldThrowOnUnknownType()) {
      const blockType = props.node?._type ?? "Couldn't determine name of type";

      throw new Error(`No serializer is defined for the following type: ${blockType}`);
    }

    return null;
  },
  button: (props: ButtonProps) => {
    if (!props.text) {
      return null;
    }

    return <SanityButton {...props} />;
  },

  /**
   * Gallery Product Callout Block
   */
  galleryProductBlock: (props: GalleryProductBlockProps) => {
    const { bundles: allBundles = [], products: allProducts = [] } = useSerializerData();

    let bundle;
    let products;

    const sanityBundle = props?.bundle as unknown as SanityBundle;
    const sanityProducts = props?.products as unknown as SanityProduct[];

    if (sanityBundle?.bundleId?.current) {
      bundle = allBundles.find(({ id }) => id === Number(sanityBundle.bundleId!.current));
    }

    if (sanityProducts?.length > 0) {
      products = sanityProducts.map(({ catalogNumber }) =>
        allProducts.find(({ sku }) => sku === catalogNumber!.current)
      );
    }

    return (
      <GalleryProductBlock
        bundle={bundle}
        content={<PortableText content={props.content!} />}
        ctaButton={props.ctaButton}
        horizontalImg={{
          alt: props?.horizontalImage?.alt ?? '',
          url: urlFor(props?.horizontalImage?.asset).url()!,
        }}
        polaroidImg={{
          alt: props?.polaroidImage?.alt ?? '',
          url: urlFor(props?.polaroidImage?.asset).url()!,
        }}
        products={products as Product[]}
        reversed={Boolean(props.isReversed)}
        title={props.title}
        trackerPageName={props.trackerPageName}
      />
    );
  },

  picture: (props: PictureProps) => {
    const router = useRouter();
    const fixedWidthParam = props.fixedImage ? `?w=${props.fixedPictureValue}` : '';

    const altText = props?.alt ? props.alt : '';

    if (!props?.asset) {
      return null;
    }

    //Default
    let finalPicture = (
      <>
        {
          <Picture
            url={urlFor(props.asset).url()!}
            alt={altText}
            polaroid={props.polaroid}
            hero={props.hero}
            className={`aspect-ratio-7by5${props.portrait ? ` xs:aspect-ratio-5by7` : ''}`}
          />
        }
      </>
    );

    if (props.asIs) {
      finalPicture = <img src={urlFor(props.asset).url()} alt={altText} />;
    }

    if (props.fixedImage) {
      finalPicture = <img loading="lazy" src={`${urlFor(props.asset).url() + fixedWidthParam}`} alt={altText} />;
    }

    if (props.link?.href) {
      if (props?.link?.includeQueryString) {
        props.link.href = appendQueryString(props.link.href, router.asPath);
      }

      return (
        <Link href={props.link.href}>
          <a className={props.link.trackerClass ?? ''}>{finalPicture}</a>
        </Link>
      );
    }

    return finalPicture;
  },

  layout1x1: (props: Layout1x1Props) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    return (
      <div className={`${props.hero ? 'p-section-hero' : 'p-section'} ${padding} ${props.backgroundColor}`}>
        <Layout1x1 hero={props.hero}>{props.children && <PortableText content={props.children} />}</Layout1x1>
      </div>
    );
  },

  layout2x1: (props: Layout2x1Props) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);
    return (
      <div className={`${props.backgroundColor} p-section ${padding}`}>
        <Layout2x1
          block1={<PortableText content={props.blockContent1} />}
          block2={<PortableText content={props.blockContent2} />}
        >
          {props.children && <PortableText content={props.children} />}
        </Layout2x1>
      </div>
    );
  },

  layout2x1ImageLeft: (props: Layout2x1ImageLeftProps) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    if (props?.bottomAlignLayout) {
      if (!props?.image?.asset) {
        return null;
      }

      const im = (
        <div className="flex h-full self-end aspect-ratio-7by5">
          <Picture
            hero
            noAspectRatioClass
            url={urlFor(props.image.asset).url()!}
            alt={props.image?.alt ? props.image?.alt : ''}
            focalPointY={0}
            className="overflow-hidden"
          />
        </div>
      );

      return (
        <div className={`${props.backgroundColor}`}>
          <Layout2x1ImageLeft hero className={`p-section-hero pb-0 ${padding}`} image={im}>
            {props.children && (
              <PortableText className={`${!props.spaceBottom ? 'pb-64' : ''}`} content={props.children} />
            )}
          </Layout2x1ImageLeft>
        </div>
      );
    }

    const content = props[`${props.mediaType}` as keyof Layout2x1ImageRightProps] as any;

    return (
      <div className={`${props.hero ? 'p-section-hero' : 'p-section'} ${padding} ${props.backgroundColor}`}>
        <Layout2x1ImageLeft hero={props.hero} image={<PortableText content={content} />}>
          {props.children && <PortableText content={props.children} />}
        </Layout2x1ImageLeft>
      </div>
    );
  },
  layout2x1ImageRight: (props: Layout2x1ImageRightProps) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    if (props?.bottomAlignLayout) {
      if (!props?.image?.asset) {
        return null;
      }

      //Moving the aspect ratio class to the outside div so the image can fill the div without the top of the image cropped out.
      const im = (
        <div className="flex h-full aspect-ratio-7by5">
          <Picture
            hero
            noAspectRatioClass
            url={urlFor(props.image.asset).url()!}
            alt={`${props.image.alt ? props.image.alt : ''}`}
            focalPointY={0}
            className="overflow-hidden"
          />
        </div>
      );

      return (
        <div className={`${props.backgroundColor}`}>
          <Layout2x1ImageRight className={`p-section-hero pb-0 ${padding}`} hero image={im}>
            {props.children && (
              <PortableText className={`${!props.spaceBottom ? 'pb-64' : ''}`} content={props.children} />
            )}
          </Layout2x1ImageRight>
        </div>
      );
    }

    const content = props[`${props.mediaType}` as keyof Layout2x1ImageRightProps] as any;

    return (
      <div className={`${props.hero ? 'p-section-hero' : 'p-section'} ${padding} ${props.backgroundColor}`}>
        <Layout2x1ImageRight hero={props.hero} image={<PortableText content={content} />}>
          {props.children && <PortableText content={props.children} />}
        </Layout2x1ImageRight>
      </div>
    );
  },
  numberedList: (props: NumberedListProps) => {
    const items = props.items.map(({ content, headerText, image }: NumberedListItemProps) => ({
      content: <PortableText content={content} />,
      headerText,
      image,
    }));

    const aboveContent = props?.content ? <PortableText content={props.content} /> : '';

    return (
      <NumberedList
        items={items}
        content={aboveContent}
        twoColumnGrid={props.twoColumnGrid}
        className={props?.backgroundColor}
      />
    );
  },
  faqItem: (props: FaqItemType) => (
    <FaqItem question={props.question} trackerClass={props.trackerClass ?? ''}>
      {props.children && <PortableText content={props.children} />}
    </FaqItem>
  ),
  layoutFaq: (props: LayoutFaqProps) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    return (
      <div className={`p-section ${props.backgroundColor} ${padding}`}>
        <LayoutFaq
          faqSingle={props.faqSingle && <PortableText content={props.faqSingle} />}
          title={props.title}
          cta={props.cta?.href && <PortableText content={[props.cta]} />}
          faqItems={props.faqItems && <PortableText content={props.faqItems} />}
          full={props.full}
        >
          {props.children && <PortableText content={props.children} />}
        </LayoutFaq>
      </div>
    );
  },
  featuredReviewItem: (props: FeaturedReviewItemType) => {
    const { fixedImage, fixedPictureValue, portrait, asset, alt } = props.image || {};
    const imgWidth: number = fixedImage && fixedPictureValue ? fixedPictureValue : portrait ? 246 : 378;
    const imgSrcSet: Breakpoints = ['lg', 'md', 'sm', 'xs', 'base'].reduce(
      (accum: { [key: string]: number }, curr: string) => {
        accum[curr] = imgWidth;
        return accum;
      },
      {}
    );

    return (
      <QuoteCallout
        quote={props.testimonial}
        citation={props.customerName}
        image={
          <div className="inline-block max-w-full border-8 border-white shadow-3 sm:border-16">
            <Picture
              url={urlFor(asset).url()!}
              alt={alt ?? ''}
              focalPointY={0}
              className={`aspect-ratio-${portrait ? '5by7' : '7by5'}`}
              srcSet={imgSrcSet}
            />
          </div>
        }
      />
    );
  },
  featuredReviews: (props: FeaturedReviewsType) => (
    <div className={`py-1 ${props.backgroundColor}`}>
      <FeaturedReviews
        title={props.title}
        featuredReviewItems={
          props.featuredReviewItems && (
            <PortableText content={props.featuredReviewItems} className="mb-64 space-y-64 md:mb-128" />
          )
        }
        showDynamicReviews={props.showDynamicReviews}
        cta={props.cta}
      />
    </div>
  ),
  nextSteps: (props: any) => (
    <div className={props.backgroundColor}>
      <NextSteps
        ctaPageName={props.ctaPageName}
        salesCallout1={getSalesCallout(props.salesCallout1, props.ctaPageName)}
        salesCallout2={getSalesCallout(props.salesCallout2, props.ctaPageName)}
      />
      {props.salesCallout1.type === 'hto' || props.salesCallout2.type === 'hto' ? <HTODisclaimer /> : null}
    </div>
  ),
  salesCalloutAlternate: (props: SalesCalloutAlternateProps) => (
    <SalesCalloutAlternateNew
      headline={props.headline!}
      image={<PortableText content={[props.image!]} />}
      {...(props.cta?.href && {
        cta: <PortableText content={[props.cta]} className="sales-callout-alternate-button" />,
      })}
    >
      {props.children && <PortableText content={props.children} />}
    </SalesCalloutAlternateNew>
  ),
  doubleHeader: (props: DoubleHeaderProps) => (
    <DoubleHeader
      smallTitle={props?.smallTitle ? props.smallTitle : ''}
      largeTitle={props?.largeTitle ? props.largeTitle : ''}
      hero={props.hero}
      boldSmallTitle={props?.boldSmallTitle}
    />
  ),
  quote: (props: QuoteProps) => (
    <Quote citationName={props.citationName} citationTitle={props.citationTitle}>
      {props.quote}
    </Quote>
  ),
  normal: ({ children }: any) => <p className="mb-16">{children}</p>,
  nospace: ({ children }: any) => <p>{children}</p>,
  sm: ({ children }: any) => <p className="text-sm mb-16">{children}</p>,
  h1: ({ children }: any) => <h1 className="text-h1 mb-16">{children}</h1>,
  h2: ({ children }: any) => <h2 className="text-h2 mb-16">{children}</h2>,
  h3: ({ children }: any) => <h3 className="text-h3 mb-16">{children}</h3>,
  h4: ({ children }: any) => <h4 className="text-h4 mb-16">{children}</h4>,
  h1_display: ({ children }: any) => <h1 className="text-h1-display">{children}</h1>,
  h2_display: ({ children }: any) => <h2 className="text-h2-display">{children}</h2>,
  h3_display: ({ children }: any) => <h3 className="text-h3-display">{children}</h3>,
  h4_display: ({ children }: any) => <h4 className="text-h4-display">{children}</h4>,
  normal_h1: ({ children }: any) => <p className="text-h1">{children}</p>,
  normal_h2: ({ children }: any) => <p className="text-h2">{children}</p>,
  normal_h3: ({ children }: any) => <p className="text-h3">{children}</p>,
  normal_h4: ({ children }: any) => <p className="text-h4">{children}</p>,
  normal_h1_display: ({ children }: any) => <p className="text-h1-display">{children}</p>,
  normal_h2_display: ({ children }: any) => <p className="text-h2-display">{children}</p>,
  normal_h3_display: ({ children }: any) => <p className="text-h3-display">{children}</p>,
  normal_h4_display: ({ children }: any) => <p className="text-h4-display">{children}</p>,

  line: () => <Line />,
  space: (props: SpaceProps) => <div className={`${props.verticalSpacingSize}`}>&nbsp;</div>,
  styleBlock: (props: StyleBlockProps) => {
    const partialWhiteBgOuter =
      props.partialWhiteBg === 'right'
        ? 'xs:bg-panel-6/12-to-right'
        : props.partialWhiteBg === 'left'
        ? 'xs:bg-panel-6/12-to-left'
        : '';
    const partialWhiteBgInner =
      props.partialWhiteBg === 'right'
        ? 'xs:bg-panel-9/12-to-right'
        : props.partialWhiteBg === 'left'
        ? 'xs:bg-panel-9/12-to-left'
        : '';

    return (
      <div className={`${props.style}${props.container ? ' container' : ''}`}>
        <div className={`${partialWhiteBgOuter}`}>
          {props?.pageBuilder?.map((item: any) => (
            <PortableText content={item} key={item._key} className={`${partialWhiteBgInner}`} />
          ))}
        </div>
      </div>
    );
  },
  link: (props: any) => {
    const router = useRouter();

    const hrefWithQueryString = props?.includeQueryString ? appendQueryString(props.href, router.asPath) : props.href;

    return (
      <a
        className={props.trackerClass}
        href={hrefWithQueryString}
        {...(props.newTab && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {props.children}
      </a>
    );
  },
  carousel: (props: any) => (
    <Carousel>
      {props.children.map((img: any) => (
        <img loading="lazy" src={`${urlFor(img.asset).url()}?w=376`} alt={img.alt} key={img._key} />
      ))}
    </Carousel>
  ),
  colorOfTheYear: (props: any) => (
    <ColorOfTheYear title={props.title} image={props.image}>
      {props?.description && <PortableText content={props.description} />}
    </ColorOfTheYear>
  ),
  gentuxem: (props: any) => <GentuxEm>{props.children}</GentuxEm>,
  htoDisclaimer: (props: any) => <HTODisclaimer textOnly={props.textOnly ?? true} />,
  layout2x2: (props: Layout2x2Props) => (
    <Layout2x2
      className={`${!props.spaceBottom ? 'mb-64' : ''} ${props.backgroundColor}`}
      block1={props.blockContent1 && <PortableText content={props.blockContent1} />}
      block2={props.blockContent2 && <PortableText content={props.blockContent2} />}
      block3={props.blockContent3 && <PortableText content={props.blockContent3} />}
      block4={props.blockContent4 && <PortableText content={props.blockContent4} />}
    />
  ),
  layout3x1: (props: Layout3x1Props) => {
    const padding = getPadding<typeof props>(props.spaceBottom, props.spaceTop);

    return (
      <div className={`p-section ${props.backgroundColor} ${padding}`}>
        <Layout3x1
          block1={
            <PortableText className="contents" content={[props.blockContent1[`${props.blockContent1.blockType}`]!]} />
          }
          block2={
            <PortableText className="contents" content={[props.blockContent2[`${props.blockContent2.blockType}`]!]} />
          }
          block3={
            <PortableText className="contents" content={[props.blockContent3[`${props.blockContent3.blockType}`]!]} />
          }
          matchHeight={props.matchHeight}
        >
          {props.children && (
            <div className={props.childrenStyle}>
              <PortableText content={props.children} />
            </div>
          )}
        </Layout3x1>
      </div>
    );
  },
  productCallout: (props: ProductCalloutType) => (
    <ProductCallout
      header={props.header}
      linkHref={props.link.href}
      linkText={props.link.title}
      newTab={props.link.newTab}
      trackerClass={props.link.trackerClass ?? ''}
      imgUrl={urlFor(props.image?.asset).url()}
      imgAlt={props.image?.alt ?? ''}
    />
  ),
  visualAsset: ({ assetType, link }: VisualAssetType) => {
    const visualAsset = getVisualAsset(assetType!);
    return link?.href ? (
      <Link href={link.href}>
        <a className={link.trackerClass ?? ''}>{visualAsset}</a>
      </Link>
    ) : (
      visualAsset
    );
  },
  video: (props: VideoProps) => <Video title={props.title} url={props.url} videotype={props.videotype} />,
  hiwItem: (props: HIWItemProps) => (
    <HIWItem
      image={<PortableText content={[props[props.mediaType]!]} />}
      description={props.description && <PortableText content={props.description} />}
    />
  ),
  tabs: (props: TabsSanityProps) => {
    if (props.items?.length > 0) {
      const data = props.items.map(({ tabTitle, content }: TabSanityProps) => ({
        content: <PortableText content={content} />,
        title: tabTitle,
      }));

      return (
        <div className="m-section-hero container flex flex-wrap items-center justify-around">
          <Tabs data={data} />
        </div>
      );
    }
  },
  textColor: (props: any) => <span className={props.color}>{props.children}</span>,
  threeImagesWithText: (props: ThreeImagesWithTextProps) => (
    <ThreeImagesWithText
      textContent={<PortableText content={props.textContent} />}
      image1={<PortableText content={[props.image1[props.image1.mediaType]!]} />}
      image2={<PortableText content={[props.image2[props.image2.mediaType]!]} />}
      image3={<PortableText content={[props.image3[props.image3.mediaType]!]} />}
    />
  ),
  collectionPageContents: () => null,
  weddingTable: (props: WeddingTableProps) => {
    const items = props.items;
    const content = props?.content ? <PortableText content={props.content} /> : undefined;
    const backgroundColor = props?.backgroundColor;

    return <WeddingTable items={items} content={content} backgroundColor={backgroundColor} />;
  },
  yotpoReviewsBadge: () => <YotpoReviewsBadge />,
  yotpoReviewsCarousel: (props: YotpoReviewsCarouselProps) => (
    <YotpoReviewsCarousel count={props.count} mode={props.mode} />
  ),
  yotpoStarRatings: (props: YotpoStarRatingsProps) => {
    return <YotpoStarRatings alignment={props.alignment} />;
  },
  yotpoUgcGallery: (props: YotpoUgcGalleryProps) => {
    const { yotpoAlbums = [], bundles = [] } = useSerializerData();
    const { header, album: albumId } = props;
    const item = props.item as unknown as SanityBundle;

    if (!item) {
      console.warn('Yotpo UGC galleries used on pages outside of /collection must have an item assigned');
      return null;
    }

    const album = yotpoAlbums.find((value) => value.id === albumId);
    const bundle = bundles.find(({ id }) => Number(item.bundleId?.current) === id);

    if (!album || !bundle) {
      console.warn('yotpoUgcGallery block is referencing a non-existent album and/or bundle');
      return null;
    }

    return <YotpoImageGallery heading={header} album={album.images} item={bundle} />;
  },
};
